// src/services/castSession.js
// Passive cast-session detector. MEASUREMENT-ONLY — this module NEVER controls
// playback or the cast session; it only OBSERVES whether the user is casting.
// See docs/decisions/adr-cast-aware-watch-tracking.md.
//
// Why this exists: watch tracking + auto-completion key off the LOCAL media
// element's play/pause/timeupdate. When a user casts to a TV, playback moves to
// the receiver and the local element goes idle, so all tracking stops. This
// detector lets the players accumulate wall-clock watch time during a cast
// session instead of recording near-zero for the most engaged viewers.
//
// Coverage — three mechanisms, each guarded so a missing API is a graceful
// no-op (a user whose cast path we can't detect simply falls back to today's
// behavior: under-count, never corruption):
//   1. Google Cast framework (Chromecast button in mux-player / media-chrome)
//   2. Remote Playback API (mediaEl.remote) — W3C standard, some Chrome paths
//   3. AirPlay (webkit wireless playback target) — Safari
//
// subscribeCastSession(getMediaEl, onChange) → unsubscribe()
//   getMediaEl: () => HTMLMediaElement | null   resolved lazily (the element may
//                                               not exist at subscribe time)
//   onChange:   (isConnected: boolean) => void  called ONLY on state CHANGES
//
// onChange never throws out (wrapped). unsubscribe() removes every listener.

export function subscribeCastSession(getMediaEl, onChange) {
    let connected = false;
    let disposed = false;
    const cleanups = [];

    const emit = (next) => {
        if (disposed) return;
        const v = !!next;
        if (v === connected) return;
        connected = v;
        try { onChange(v); } catch (_) { /* never throw out of a listener */ }
    };

    // ── 1. Google Cast framework (Chromecast) ── global, element-independent.
    try {
        const fw = (typeof window !== 'undefined') && window.cast && window.cast.framework;
        if (fw && fw.CastContext && fw.SessionState && fw.CastContextEventType) {
            const ctx = fw.CastContext.getInstance();
            const onSession = (event) => {
                const s = event && event.sessionState;
                if (s === fw.SessionState.SESSION_STARTED || s === fw.SessionState.SESSION_RESUMED) emit(true);
                else if (s === fw.SessionState.SESSION_ENDED) emit(false);
            };
            ctx.addEventListener(fw.CastContextEventType.SESSION_STATE_CHANGED, onSession);
            cleanups.push(() => {
                try { ctx.removeEventListener(fw.CastContextEventType.SESSION_STATE_CHANGED, onSession); } catch (_) { /* defensive */ }
            });
            // Seed: a session may already be active when we subscribe.
            try { if (ctx.getCurrentSession && ctx.getCurrentSession()) emit(true); } catch (_) { /* defensive */ }
        }
    } catch (_) { /* Cast framework absent — graceful no-op */ }

    // ── 2. Remote Playback API + 3. AirPlay ── element-scoped. The element may
    // not exist yet (mux-player builds its inner <video> async), so poll briefly.
    let attached = false;
    const attachElementListeners = () => {
        if (disposed || attached) return false;
        const el = getMediaEl();
        if (!el) return false;
        attached = true;

        // Remote Playback API (covers AirPlay on Safari + Chrome remote playback).
        try {
            if (el.remote && typeof el.remote.addEventListener === 'function') {
                const onConnect = () => emit(true);
                const onDisconnect = () => emit(false);
                el.remote.addEventListener('connect', onConnect);
                el.remote.addEventListener('connecting', onConnect);
                el.remote.addEventListener('disconnect', onDisconnect);
                cleanups.push(() => {
                    try { el.remote.removeEventListener('connect', onConnect); } catch (_) { /* defensive */ }
                    try { el.remote.removeEventListener('connecting', onConnect); } catch (_) { /* defensive */ }
                    try { el.remote.removeEventListener('disconnect', onDisconnect); } catch (_) { /* defensive */ }
                });
                try { if (el.remote.state === 'connected') emit(true); } catch (_) { /* defensive */ }
            }
        } catch (_) { /* Remote Playback API absent — graceful */ }

        // AirPlay (Safari webkit) — separate signal from the Remote Playback API.
        try {
            const onWireless = () => emit(!!el.webkitCurrentPlaybackTargetIsWireless);
            el.addEventListener('webkitcurrentplaybacktargetiswirelesschanged', onWireless);
            cleanups.push(() => {
                try { el.removeEventListener('webkitcurrentplaybacktargetiswirelesschanged', onWireless); } catch (_) { /* defensive */ }
            });
            if (el.webkitCurrentPlaybackTargetIsWireless) emit(true);
        } catch (_) { /* webkit AirPlay API absent — graceful */ }

        return true;
    };

    // Try immediately; if the element isn't ready, retry on a bounded interval
    // (every 500ms for ~15s). Self-clears once attached or on dispose.
    if (!attachElementListeners()) {
        let tries = 0;
        const pollId = setInterval(() => {
            if (disposed || attachElementListeners() || tries >= 30) {
                clearInterval(pollId);
            }
            tries += 1;
        }, 500);
        cleanups.push(() => { try { clearInterval(pollId); } catch (_) { /* defensive */ } });
    }

    return () => {
        disposed = true;
        cleanups.forEach((fn) => { try { fn(); } catch (_) { /* defensive */ } });
    };
}
